import React, { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { Tooltip, Button, Menu, MenuItem, TextField, InputAdornment, Box } from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
} from '@mui/x-data-grid-pro';
import CircularLoading from '../CircularLoading';
import PropTypes from 'prop-types';
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import { PrimaryButton, SecondaryButton, ScanButton } from "../../Common";
import { TranslateW } from "../../../Utils/Languages/Translate";
import { EXPORT_TIMEOUT_ERROR, EXPORT_GENERIC_ERROR } from "../../../utils";

const exportType = {
    pdf: "pdf",
    csv: "csv",
    xlsm: "xlsm",
    xlsx: "xlsx"
};

const getMimeType = (type) => {
    switch (type) {
        case exportType.pdf:
            return "application/pdf";
        case exportType.xlsm:
        case exportType.xlsx:
            return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        default:
            return "text/csv";
    }
};

const getFileName = (type, customFileName) => {
    switch (type) {
        case exportType.pdf:
            return customFileName ? `${customFileName}_${Date.now()}.pdf` : `ServiceEvents_${Date.now()}.pdf`;
        case exportType.xlsm:
            return `${customFileName}.xlsm`;
        case exportType.xlsx:
            return `${customFileName}.xlsx`;
        default:
            return `${customFileName}.csv`;
    }
};

/*
 * The component below is used only for
 * DataGrid & DataGridWithExpansionPanel
*/

const ToolBar = ({
    exportButtonTooltip,
    exportFileTypes,
    exportOptions,
    hideExportButton,
    hideFilterButton,
    enableExport,
    renderEditButton,
    editOnClick,
    cancelOnClick,
    editActive,
    saveDisabled,
    setEditActive,
    toolbarLeftContent,
    showScanButton,
    onScanClick,
    searchValue,
    onSearchChange,
    searchPlaceholder,
    toolbarRightContent
}) => {
    const [exportLoading, setExportLoading] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const searchDebounceRef = useRef(null);

    // Table search (AB#17146 / 17128 AC 13): debounced so the grid refetches on a typing
    // pause, not per keystroke. The input is uncontrolled (defaultValue) because the grid
    // recreates its toolbar slot per render; the committed term lives in the DataGrid.
    const handleSearchInput = (event) => {
        const value = event.target.value;
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => onSearchChange(value), 400);
    };

    const handleExport = async (fileType) => {
        const {
            exportURL,
            rowCount = 10,
            page = 1,
            sort,
            sortBy,
            filters,
            type,
            exportFileName,
            searchTerm,
            getSearchFields,
            getExportColumns
        } = exportOptions;

        if (!exportURL) return toast.error(EXPORT_GENERIC_ERROR, { duration: 5000 });
        setAnchorEl(null);
        setExportLoading(true);

        // Resolved at click time: the visible-column set must be the one on screen when
        // Export is clicked, not when this toolbar last rendered (AB#17288).
        const searchFields = getSearchFields?.();
        const exportColumns = getExportColumns?.();

        const response = await fetch(exportURL, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                rowCount,
                page,
                sortBy: sortBy ?? sort,
                filters,
                exportType: fileType,
                ...(searchTerm ? { searchTerm, searchFields } : {}),
                ...(exportColumns ? { exportColumns } : {}),
            }),
            responseType: 'blob'
        }).catch(() => null);

        if (!response) {
            toast.error(EXPORT_GENERIC_ERROR, { duration: 5000 });
            return setExportLoading(false);
        }

        if (response.status === 599) {
            toast.error("Export list too large.  Reduce size to less than 1000.", { duration: 5000 });
            return setExportLoading(false);
        }

        if (response.status === 504) {
            toast.error(EXPORT_TIMEOUT_ERROR, { duration: 5000 });
            return setExportLoading(false);
        }

        if (!response.ok) {
            toast.error(EXPORT_GENERIC_ERROR, { duration: 5000 });
            return setExportLoading(false);
        }

        const data = await response.blob();

        const customFileName = exportFileName || Date.now();
        const url = window.URL.createObjectURL(data, { type: getMimeType(fileType) });
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
            'download',
            getFileName(fileType, customFileName)
        );
        document.body.appendChild(link);
        link.click();
        return setExportLoading(false);
    };
    
    const cancelEdit = () => {
        cancelOnClick();
        return setEditActive(false);
    };

    return (
        // Right-side content needs the row to span the grid, or ml: 'auto' has nothing to push against (AB#17128 AC13)
        <GridToolbarContainer sx={{ width: "100%", ...(toolbarRightContent && { flex: 1, minWidth: 0 }) }}>
            {toolbarLeftContent}
            <GridToolbarColumnsButton />
            {!hideFilterButton && <GridToolbarFilterButton />}
            {showScanButton && (
                <ScanButton
                    onClick={onScanClick}
                    label={TranslateW("ScanButton")}
                    tooltipText={TranslateW("ScanFindAssetTooltip")}
                    sx={{ fontSize: "13px" }}
                />
            )}
            {!hideExportButton && (
                exportFileTypes.length === 1 ? (

                    <Tooltip
                        title={exportButtonTooltip}
                    >
                        <Button
                            size='medium'
                            startIcon={exportLoading
                                ? <CircularLoading marginLeft={1} size={15} />
                                : <FileUploadOutlinedIcon />
                            }
                            onClick={() => enableExport && handleExport('.csv')}
                            disabled={exportLoading}
                            sx={{ fontSize: "13px" , display: { xs: 'none', lg: 'inline-flex' }}}
                        >
                            {exportLoading ? "Exporting..." : "Export"}
                        </Button>
                    </Tooltip>
                ) : (
                    <>
                        <Tooltip title={'Multiple Export Options'}>
                            <Button
                                startIcon={exportLoading
                                    ? <CircularLoading marginLeft={1} size={15} />
                                    : <FileUploadOutlinedIcon />
                                }
                                onClick={(e) => enableExport && setAnchorEl(e.currentTarget)}
                                disabled={exportLoading}
                                sx={{ fontSize: "13px" }}
                            >
                                {exportLoading ? "Exporting..." : "Export"}
                            </Button>
                        </Tooltip>
                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={() => setAnchorEl(null)}
                        >
                            {exportFileTypes.map((ft) => (
                                <MenuItem
                                    key={ft.type}
                                    onClick={() => handleExport(ft.type)}
                                >
                                    {`Export as ${ft.name} (.${ft.type})`}
                                </MenuItem>
                            ))}
                        </Menu>
                    </>
                )
            )}
            {onSearchChange && (
                <TextField
                    size="small"
                    type="search"
                    placeholder={searchPlaceholder}
                    defaultValue={searchValue}
                    onChange={handleSearchInput}
                    inputProps={{ 'aria-label': 'Search table' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        marginLeft: 'auto',
                        marginRight: 1,
                        width: { xs: '150px', sm: '220px' },
                        '& .MuiInputBase-root': { fontSize: '13px', height: '32px' },
                    }}
                />
            )}
            {renderEditButton && (
                <div style={{ display: "flex", marginLeft: "auto", justifyContent: "space-between" }}>
                    {!editActive && (
                        <SecondaryButton
                            variant={"outlined"}
                            buttonText={"Edit"}
                            onClick={() => setEditActive(true)}
                            startIcon={<ModeEditIcon />}
                        />
                    )}
                    {editActive && (
                        <>
                            <PrimaryButton
                                buttonText={"Save"}
                                onClick={editOnClick}
                                disabled={saveDisabled}
                            />

                            <SecondaryButton
                                buttonText={"Cancel"}
                                onClick={cancelEdit}
                                sx={{ marginLeft: "3%" }}
                            />
                        </>
                    )}
                </div>
            )}
            {toolbarRightContent && (
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>{toolbarRightContent}</Box>
            )}
        </GridToolbarContainer>
    );
};

ToolBar.propTypes = {
    exportButtonTooltip: PropTypes.string,
    exportOptions: PropTypes.object,
    hideExportButton: PropTypes.bool,
    enableExport: PropTypes.bool,
    renderEditButton: PropTypes.bool,
    editOnClick: PropTypes.func,
    cancelOnClick: PropTypes.func,
    editActive: PropTypes.bool,
    saveDisabled: PropTypes.bool,
    setEditActive: PropTypes.func,
    showScanButton: PropTypes.bool,
    onScanClick: PropTypes.func,
    searchValue: PropTypes.string,
    onSearchChange: PropTypes.func,
    searchPlaceholder: PropTypes.string,
    toolbarRightContent: PropTypes.node,
};

ToolBar.defaultProps = {
    exportButtonTooltip: "Export Data",
    exportOptions: {},
    hideExportButton: false,
    enableExport: true,
    renderEditButton: false,
    editOnClick: () => {},
    cancelOnClick: () => {},
    editActive: false,
    saveDisabled: false,
    setEditActive: () => {},
    showScanButton: false,
    onScanClick: () => {},
    searchValue: '',
    onSearchChange: null,
    searchPlaceholder: 'Search',
};

export default ToolBar;
